"use client";

import * as React from "react";
import { AuthModal } from "@/components/auth/auth-modal";

type AuthTab = "signin" | "signup";

interface AuthModalContextValue {
  openAuthModal: (tab?: AuthTab) => void;
  closeAuthModal: () => void;
  isAuthModalOpen: boolean;
  authModalTab: AuthTab;
}

const AuthModalContext = React.createContext<AuthModalContextValue | undefined>(
  undefined,
);

export function AuthModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tab, setTab] = React.useState<AuthTab>("signin");

  const openAuthModal = React.useCallback((newTab?: AuthTab) => {
    if (newTab) {
      setTab(newTab);
    }
    setIsOpen(true);
  }, []);

  const closeAuthModal = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSuccess = React.useCallback(() => {
    setIsOpen(false);
    // Allow modal to close before refreshing
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, []);

  return (
    <AuthModalContext.Provider
      value={{
        openAuthModal,
        closeAuthModal,
        isAuthModalOpen: isOpen,
        authModalTab: tab,
      }}
    >
      {children}
      <AuthModal
        isOpen={isOpen}
        onClose={closeAuthModal}
        defaultTab={tab}
        onSuccess={handleSuccess}
      />
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const context = React.useContext(AuthModalContext);
  if (!context) {
    throw new Error("useAuthModal must be used within AuthModalProvider");
  }
  return context;
}
